import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";

export default function PermissionMultiSelect({
    label,
    options,
    selectedIds,
    onChangeSelectedIds,
    searchPlaceholder,
}) {
    const anchorRef = useRef(null);
    const chipsContainerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(selectedIds.length);

    const optionsById = useMemo(() => {
        const map = new Map();
        options.forEach((option) => {
            map.set(option.id, option.name);
        });
        return map;
    }, [options]);

    const filteredOptions = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        if (!normalizedSearch) {
            return options;
        }

        return options.filter((option) =>
            option.name.toLowerCase().includes(normalizedSearch),
        );
    }, [options, search]);

    // Calcula dinamicamente quantas chips cabem no espaço disponível
    useLayoutEffect(() => {
        const container = chipsContainerRef.current;
        if (!container || selectedIds.length === 0) {
            setVisibleCount(selectedIds.length);
            return;
        }

        const measure = () => {
            const containerWidth = container.offsetWidth;
            // Espaço reservado para o botão X (40px) + seta (20px) = ~60px
            const availableWidth = containerWidth;
            const counterWidth = 50; // largura estimada do "+N"
            const gap = 4;

            let totalWidth = 0;
            let count = 0;

            const chips = container.querySelectorAll('[data-chip="true"]');

            for (let i = 0; i < chips.length; i++) {
                const chipWidth = chips[i].offsetWidth;
                const remaining = selectedIds.length - (count + 1);
                const needsCounter = remaining > 0;
                const requiredSpace =
                    totalWidth +
                    chipWidth +
                    (count > 0 ? gap : 0) +
                    (needsCounter ? counterWidth + gap : 0);

                if (requiredSpace <= availableWidth) {
                    totalWidth += chipWidth + (count > 0 ? gap : 0);
                    count++;
                } else {
                    break;
                }
            }

            // Garante pelo menos 1 chip visível
            setVisibleCount(Math.max(1, count));
        };

        // Aguarda as chips renderizarem para medir
        setTimeout(measure, 0);

        const observer = new ResizeObserver(measure);
        observer.observe(container);

        return () => observer.disconnect();
    }, [selectedIds]);

    const handleSelectAll = () => {
        onChangeSelectedIds(options.map((option) => option.id));
    };

    const handleToggleOption = (id) => {
        if (selectedIds.includes(id)) {
            onChangeSelectedIds(selectedIds.filter((itemId) => itemId !== id));
            return;
        }

        onChangeSelectedIds([...selectedIds, id]);
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                ref={anchorRef}
                onClick={() => setOpen(true)}
                sx={{
                    position: "relative",
                    border: "1px solid rgba(0, 0, 0, 0.23)",
                    borderRadius: "4px",
                    padding: "6px 12px",
                    minHeight: "48px",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    "&:hover": {
                        borderColor: "rgba(0, 0, 0, 0.87)",
                    },
                }}
            >
                {/* Label flutuante */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "-9px",
                        left: "8px",
                        backgroundColor: "#fff",
                        padding: "0 4px",
                        fontSize: "12px",
                        color: "rgba(0, 0, 0, 0.6)",
                    }}
                >
                    {label}
                </Box>

                {/* Chips */}
                <Box
                    ref={chipsContainerRef}
                    sx={{
                        display: "flex",
                        gap: "4px",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        flex: 1,
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    {selectedIds.length > 0 ? (
                        <>
                            {selectedIds.map((id, index) => {
                                const name = optionsById.get(id);
                                if (!name) return null;

                                const isVisible = index < visibleCount;

                                return (
                                    <Chip
                                        key={id}
                                        data-chip="true"
                                        size="small"
                                        label={name}
                                        onDelete={(event) => {
                                            event.stopPropagation();
                                            onChangeSelectedIds(
                                                selectedIds.filter(
                                                    (item) => item !== id,
                                                ),
                                            );
                                        }}
                                        onMouseDown={(event) => {
                                            event.stopPropagation();
                                        }}
                                        sx={{
                                            maxWidth: "200px",
                                            "& .MuiChip-label": {
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                                whiteSpace: "nowrap",
                                            },
                                            ...(isVisible
                                                ? {}
                                                : {
                                                      position: "absolute",
                                                      visibility: "hidden",
                                                      pointerEvents: "none",
                                                  }),
                                        }}
                                    />
                                );
                            })}
                            {selectedIds.length > visibleCount && (
                                <Chip
                                    size="small"
                                    label={`+${selectedIds.length - visibleCount}`}
                                    sx={{
                                        backgroundColor: "#e0e0e0",
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                        </>
                    ) : (
                        <Box sx={{ height: "24px" }} />
                    )}
                </Box>

                {/* Botões direita */}
                <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                    {selectedIds.length > 0 && (
                        <IconButton
                            size="small"
                            aria-label="limpar seleção"
                            onMouseDown={(event) => {
                                event.preventDefault();
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                onChangeSelectedIds([]);
                            }}
                            sx={{ padding: "4px" }}
                        >
                            X
                        </IconButton>
                    )}
                </Box>
            </Box>

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                sx={{
                    zIndex: 1500,
                    width: anchorRef.current
                        ? anchorRef.current.clientWidth
                        : undefined,
                }}
            >
                <ClickAwayListener
                    onClickAway={() => {
                        setOpen(false);
                    }}
                >
                    <Paper
                        sx={{
                            mt: 0.5,
                            backgroundColor: "#fff",
                            border: "1px solid #d9d9d9",
                        }}
                    >
                        <Box
                            sx={{
                                p: 1,
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#fff",
                            }}
                        >
                            <TextField
                                size="small"
                                fullWidth
                                autoFocus
                                value={search}
                                placeholder={
                                    searchPlaceholder || "Buscar opção"
                                }
                                onChange={(event) => {
                                    setSearch(event.target.value);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Escape") {
                                        setOpen(false);
                                    }
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "rgba(0, 0, 0, 0.87)",
                                        borderWidth: "1px",
                                    },
                                }}
                                InputProps={{
                                    endAdornment: search ? (
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearch("")}
                                            sx={{ padding: "4px" }}
                                        >
                                            X
                                        </IconButton>
                                    ) : null,
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                maxHeight: 220,
                                overflowY: "auto",
                                backgroundColor: "#fff",
                            }}
                        >
                            {search.trim() === "" && (
                                <Box
                                    onClick={handleSelectAll}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        px: 2.5,
                                        py: 2,
                                        cursor: "pointer",
                                        borderBottom: "1px solid #e0e0e0",
                                        "&:hover": {
                                            backgroundColor: "action.hover",
                                        },
                                    }}
                                >
                                    <span
                                        style={{
                                            color: "#419392",
                                            fontWeight: 500,
                                        }}
                                    >
                                        SELECIONAR TODOS
                                    </span>
                                </Box>
                            )}

                            {filteredOptions.map((option) => {
                                const checked = selectedIds.includes(option.id);

                                return (
                                    <Box
                                        key={option.id}
                                        onClick={() => {
                                            handleToggleOption(option.id);
                                        }}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            px: 1,
                                            py: 1,
                                            cursor: "pointer",
                                            "&:hover": {
                                                backgroundColor: "action.hover",
                                            },
                                        }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={checked}
                                        />
                                        <span>{option.name}</span>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
}
